import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetSite } from 'src/features/sites/api/useGetSite';

import { Separator } from 'src/registry/ui/Separator';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { generalMessages } from 'src/utils/messages';
import { useTitleStore } from 'src/stores/useTitleStore';
import {
    GeneralBlock,
    GeneralBlockBody,
    GeneralBlockHeader,
    GeneralBlockTitle,
} from 'src/registry/components/blocks/GeneralBlock';
import { Badge } from 'src/registry/ui/Badge';
import { COUNTRY_TRANSLATION_MAP, siteBadgeOptions } from 'src/features/sites/utils/constants';
import { siteMessages } from 'src/features/sites/utils/messages';
import { useParking } from 'src/providers/ParkingProvider';
import GeneralBlockAttribute from 'src/registry/components/blocks/GeneralBlockAttribute';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'src/registry/ui/Tabs';
import { workingHoursMessages } from 'src/features/workingHours/utils/messages';
import WorkingHours from 'src/features/workingHours/components/WorkingHours';
import WorkingHoursExceptions from 'src/features/workingHoursExceptions/components/WorkingHoursExceptions';
import SitePricingPlans from 'src/features/pricing/sites/components/SitePricingPlans';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import BackButton from 'src/features/navigation/components/BackButton';
import { PATH } from 'src/features/navigation/utils/constants';

export default function SiteDashboard() {
    const { formatMessage, locale } = useIntl();
    const { siteId = '' } = useParams();
    const { parkingId } = useParking();
    const accessSites = useHasPermissions([PERMISSION.ACCESS_SITES]);
    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const defaultTabsValue = (() => {
        if (accessSites) return 'working-hours';
        if (managePricingPlans) return 'pricing-plans';
    })();

    const { data: siteData, isLoading } = useGetSite({ siteId, parkingId });

    const { setTitle, removeTitle } = useTitleStore();
    React.useEffect(() => {
        if (!siteData) return;
        setTitle(siteData.translations.find((translation) => translation.language === locale)?.name ?? '');
        return removeTitle;
    }, [locale, removeTitle, setTitle, siteData]);

    const siteType = React.useMemo(() => (siteData ? siteBadgeOptions[siteData.type] : null), [siteData]);

    const siteCoordinates = siteData?.coordinates?.coordinates
        ? `${siteData?.coordinates.coordinates[0]}, ${siteData?.coordinates.coordinates[1]}`
        : '-';

    return (
        <>
            <BackButton customRoute={`/${PATH.ADMINISTRATION}/site`} />
            <GeneralBlock>
                <div className="grid flex-3 grid-cols-3 gap-4">
                    {siteData?.translations.map((translation) => {
                        const languageToUpper = translation.language.toUpperCase();
                        return (
                            <React.Fragment key={translation.id}>
                                <GeneralBlockAttribute
                                    label={`${formatMessage(generalMessages.name)} (${languageToUpper})`}
                                    value={translation.name}
                                />

                                <GeneralBlockAttribute
                                    label={`${formatMessage(siteMessages.address)} (${languageToUpper})`}
                                    value={translation.address}
                                />
                                <GeneralBlockAttribute
                                    label={`${formatMessage(siteMessages.city)} (${languageToUpper})`}
                                    value={translation.city}
                                />
                            </React.Fragment>
                        );
                    })}
                </div>
                <Separator />
                <div className="grid flex-3 grid-cols-3 gap-4">
                    <GeneralBlockAttribute label={formatMessage(siteMessages.coordinates)} value={siteCoordinates} />
                    <div className="flex flex-col">
                        <GeneralBlockHeader variant="dashboard">
                            <GeneralBlockTitle>{formatMessage(siteMessages.type)}</GeneralBlockTitle>
                        </GeneralBlockHeader>
                        <GeneralBlockBody variant="dashboard">
                            {isLoading || !siteType ? (
                                <Skeleton className="h-6 w-full max-w-48" />
                            ) : (
                                <Badge theme={siteType.theme}>
                                    {siteType.icon && <FontAwesomeIcon icon={siteType.icon} />}
                                    {formatMessage(siteType.label)}
                                </Badge>
                            )}
                        </GeneralBlockBody>
                    </div>
                    <GeneralBlockAttribute
                        label={formatMessage(siteMessages.totalSpaces)}
                        value={siteData?.total_spaces}
                    />
                    <GeneralBlockAttribute label={formatMessage(siteMessages.floorsCount)} value={siteData?.floors} />
                    <GeneralBlockAttribute
                        label={formatMessage(siteMessages.country)}
                        value={siteData ? formatMessage(COUNTRY_TRANSLATION_MAP[siteData.country]) : ''}
                    />
                    <GeneralBlockAttribute
                        label={formatMessage(siteMessages.organisation)}
                        value={siteData?.service_provider.name}
                    />
                    <div className="flex flex-col">
                        <GeneralBlockHeader variant="dashboard">
                            <GeneralBlockTitle>{formatMessage(siteMessages.is247)}</GeneralBlockTitle>
                        </GeneralBlockHeader>
                        <GeneralBlockBody variant="dashboard">
                            {isLoading || !siteType ? (
                                <Skeleton className="h-6 w-full max-w-48" />
                            ) : (
                                <Badge>
                                    <div
                                        className={`size-1.5 rounded-full ${siteData?.is_24_7 ? 'bg-green-600' : 'bg-red-600'}`}
                                    />
                                    {formatMessage(siteData?.is_24_7 ? generalMessages.yes : generalMessages.no)}
                                </Badge>
                            )}
                        </GeneralBlockBody>
                    </div>
                </div>
            </GeneralBlock>
            <Tabs defaultValue={defaultTabsValue}>
                <TabsList>
                    {accessSites && (
                        <>
                            <TabsTrigger value="working-hours">
                                <FormattedMessage
                                    defaultMessage={workingHoursMessages.workingHours.defaultMessage}
                                    id={workingHoursMessages.workingHours.id}
                                />
                            </TabsTrigger>
                            <TabsTrigger value="working-hours-exceptions">
                                <FormattedMessage
                                    defaultMessage={workingHoursMessages.workingHoursExceptions.defaultMessage}
                                    id={workingHoursMessages.workingHoursExceptions.id}
                                />
                            </TabsTrigger>
                        </>
                    )}

                    {managePricingPlans && (
                        <TabsTrigger value="pricing-plans">
                            <FormattedMessage
                                defaultMessage={siteMessages.pricingPlans.defaultMessage}
                                id={siteMessages.pricingPlans.id}
                            />
                        </TabsTrigger>
                    )}
                </TabsList>
                {accessSites && (
                    <>
                        <TabsContent value="working-hours">
                            <WorkingHours siteId={siteId} />
                        </TabsContent>
                        <TabsContent value="working-hours-exceptions">
                            <WorkingHoursExceptions siteId={siteId} />
                        </TabsContent>
                    </>
                )}
                {managePricingPlans && (
                    <TabsContent value="pricing-plans">
                        <SitePricingPlans siteId={siteId} />
                    </TabsContent>
                )}
            </Tabs>
        </>
    );
}
